import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Typography,
  Button,
  Table,
  Tag,
  Spin,
  message,
  Space,
  Tooltip,
  Badge,
  Divider,
  Alert,
  Breadcrumb,
  Modal,
  Descriptions,
  Pagination
} from 'antd';
import {
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined,
  LinkOutlined,
  EyeOutlined,
  ExportOutlined,
  ArrowLeftOutlined,
  HomeOutlined
} from '@ant-design/icons';
import LandingLayout from './LandingLayout';
import './IssuerNewsDetails.css';
import apiClient from '../../services/apiClient';

const { Title, Text, Paragraph } = Typography;

const IssuerNewsDetails = () => {
  const { issuerName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [newsData, setNewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Get issuer name from URL params or location state
  const getIssuerName = () => {
    if (issuerName) {
      return decodeURIComponent(issuerName);
    }
    if (location.state?.issuerName) {
      return location.state.issuerName;
    }
    return "CANARA BANK";
  };

  const currentIssuerName = getIssuerName();

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  // Fetch news data
  const fetchNewsData = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${baseUrl}/news/issuer/${encodeURIComponent(currentIssuerName)}?page=${page - 1}&size=${pageSize}`
      );
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;

      if (result.statusCode === 200 && result.data) {
        setNewsData(result.data);
        setPagination({
          current: (result.data.pageNumber || 0) + 1,
          pageSize: result.data.pageSize || 10,
          total: result.data.totalElements || 0
        });
        console.log('News API Response:', result);
      } else {
        console.error('Invalid news API response structure:', result);
        setNewsData({ content: [], totalElements: 0 });
      }
    } catch (error) {
      console.error('Error fetching news data:', error);
      setError("Error fetching news data");
      message.error("Error fetching news data");
      setNewsData({ content: [], totalElements: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsData();
  }, [currentIssuerName]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };

  // Handle news click
  const handleNewsClick = (news) => {
    setSelectedNews(news);
    setModalVisible(true);
  };

  // Handle external link
  const handleExternalLink = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Handle pagination change
  const handlePaginationChange = (page, pageSize) => {
    fetchNewsData(page, pageSize);
  };

    // Table columns
  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: '14px', color: '#1a202c', lineHeight: '1.4' }}>
            {text || 'N/A'}
          </Text>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', lineHeight: '1.4' }}>
              {record.description.length > 150 
                ? record.description.substring(0, 150) + '...' 
                : record.description}
            </div>
          )}
        </div>
      ),
      width: '45%',
      ellipsis: true
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (text) => (
        <Tag color="blue" style={{ fontWeight: '500', fontSize: '12px' }}>
          {text || 'N/A'}
        </Tag>
      ),
      width: '12%',
      ellipsis: true
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      render: (text) => (
        <Text type="secondary" style={{ fontSize: '13px' }}>
          {text || 'N/A'}
        </Text>
      ),
      width: '15%',
      ellipsis: true
    },
    {
      title: 'Published Date',
      dataIndex: 'publishedDate',
      key: 'publishedDate',
      render: (text) => (
        <Space direction="vertical" size={2}>
          <Text type="secondary" style={{ fontSize: '13px', fontWeight: '500' }}>
            {formatDate(text)}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            <CalendarOutlined style={{ color: '#64748b', marginRight: '4px' }} />
            Published
          </Text>
        </Space>
      ),
      width: '13%'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space direction="vertical" size={8}>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleNewsClick(record)}
            style={{ width: '100%' }}
          >
            View Details
          </Button>
          {record.url && (
            <Button
              type="default"
              size="small"
              icon={<ExportOutlined />}
              onClick={() => handleExternalLink(record.url)}
              style={{ width: '100%' }}
            >
              Open Source
            </Button>
          )}
        </Space>
      ),
      width: '15%'
    }
  ];

  if (loading) {
    return (
      <LandingLayout>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Spin size="large" />
          <div style={{ marginTop: '1rem' }}>Loading news...</div>
        </div>
      </LandingLayout>
    );
  }

  if (error) {
    return (
      <LandingLayout>
        <div style={{ padding: '20px' }}>
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" danger onClick={() => fetchNewsData()}>
                Retry
              </Button>
            }
          />
        </div>
      </LandingLayout>
    );
  }

  return (
    <LandingLayout>
      <div className="news-details-container">
        {/* Header Section */}
        <div className="news-details-header">
          <div className="news-details-header-content">
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                className="back-button"
              >
                Back
              </Button>
            </div>
            <div className="news-title-section">
              <Title level={2} className="news-page-title">
                News Details
              </Title>
              <div className="issuer-info">
                <Text strong style={{ fontSize: '16px', color: '#64748b' }}>
                  {currentIssuerName}
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="breadcrumb-section">
          <Breadcrumb
            items={[
              {
                title: <HomeOutlined />,
                onClick: () => navigate('/')
              },
              {
                title: 'Issuers',
                onClick: () => navigate('/issuers')
              },
              {
                title: currentIssuerName,
                onClick: () => navigate(-1)
              },
              {
                title: 'News Details'
              }
            ]}
          />
        </div>

        {/* Content */}
        <div className="news-details-content">
          <Card className="news-details-card">
            {newsData?.content && newsData.content.length > 0 ? (
              <>
                <Table
                  dataSource={newsData.content}
                  columns={columns}
                  pagination={false}
                  rowKey={(record, index) => record.id || index}
                  className="news-table"
                  size="middle"
                />
                
                <div style={{ 
                  marginTop: '24px', 
                  textAlign: 'center',
                  padding: '16px',
                  borderTop: '1px solid #e2e8f0',
                  background: '#f8fafc',
                  borderRadius: '0 0 12px 12px'
                }}>
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePaginationChange}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(total, range) => 
                      `${range[0]}-${range[1]} of ${total} news items`
                    }
                    style={{ 
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  />
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📰</div>
                <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '0.5rem' }}>
                  No News Found
                </div>
                <div style={{ fontSize: '14px' }}>
                  This issuer doesn't have any news available yet.
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* News Details Modal */}
        <Modal
          title="News Details"
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setModalVisible(false)}>
              Close
            </Button>,
            selectedNews?.url && (
                           <Button
               key="external"
               type="primary"
               icon={<ExportOutlined />}
               onClick={() => handleExternalLink(selectedNews.url)}
             >
               Open Source
             </Button>
            )
          ]}
          width={800}
        >
          {selectedNews && (
            <div className="news-details-content">
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Title" span={3}>
                  <Text strong>{selectedNews.title || 'N/A'}</Text>
                </Descriptions.Item>
                
                <Descriptions.Item label="Description" span={3}>
                  <Paragraph>{selectedNews.description || 'N/A'}</Paragraph>
                </Descriptions.Item>
                
                <Descriptions.Item label="Subtitle">
                  {selectedNews.subtitle || 'N/A'}
                </Descriptions.Item>
                
                <Descriptions.Item label="Author">
                  {selectedNews.author || 'N/A'}
                </Descriptions.Item>
                
                <Descriptions.Item label="Source">
                  <Tag color="blue">{selectedNews.source || 'N/A'}</Tag>
                </Descriptions.Item>
                
                <Descriptions.Item label="Company">
                  <Tag color="green">{selectedNews.company || 'N/A'}</Tag>
                </Descriptions.Item>
                
                <Descriptions.Item label="Published Date">
                  <Space>
                    <CalendarOutlined />
                    {formatDate(selectedNews.publishedDate)}
                  </Space>
                </Descriptions.Item>
                
                <Descriptions.Item label="Time">
                  {selectedNews.time || 'N/A'}
                </Descriptions.Item>
                
                <Descriptions.Item label="Topics">
                  {selectedNews.topics || 'N/A'}
                </Descriptions.Item>
                
                <Descriptions.Item label="URL" span={3}>
                  {selectedNews.url ? (
                    <Button
                      type="link"
                      icon={<LinkOutlined />}
                      onClick={() => handleExternalLink(selectedNews.url)}
                    >
                      {selectedNews.url}
                    </Button>
                  ) : (
                    'N/A'
                  )}
                </Descriptions.Item>
                
                <Descriptions.Item label="Scraped At">
                  {selectedNews.scrapedAt ? new Date(selectedNews.scrapedAt).toLocaleString() : 'N/A'}
                </Descriptions.Item>
                
                <Descriptions.Item label="Created At">
                  {selectedNews.createdAt ? new Date(selectedNews.createdAt).toLocaleString() : 'N/A'}
                </Descriptions.Item>
                
                <Descriptions.Item label="Updated At">
                  {selectedNews.updatedAt ? new Date(selectedNews.updatedAt).toLocaleString() : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}
        </Modal>
      </div>
    </LandingLayout>
  );
};

export default IssuerNewsDetails;
